import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo-evar.jpg';
import { formatPrice } from '../data/products';
import { getCartSubtotal } from '../data/cart';
import { useShipping } from '../data/useShipping';
import { usePayment } from '../data/usePayment';
import { waLink } from '../data/store';
import './Checkout.css';

const EMPTY_FORM = {
  fullName: '',
  phone: '',
  whatsapp: '',
  governorate: '',
  city: '',
  address: '',
  notes: '',
};

const PHONE_PATTERN = /^01\d{9}$/;
const digitsOnly = (value) => value.replace(/\D/g, '').slice(0, 11);

function validateDetails(form) {
  const errors = {};
  if (form.fullName.trim().length < 3) errors.fullName = 'Write your full name.';
  if (!PHONE_PATTERN.test(form.phone)) errors.phone = 'The number must be 11 digits and start with 01.';
  if (form.whatsapp && !PHONE_PATTERN.test(form.whatsapp)) {
    errors.whatsapp = 'The WhatsApp number must be 11 digits and start with 01.';
  }
  if (!form.governorate) errors.governorate = 'Choose your governorate.';
  if (!form.city.trim()) errors.city = 'Write your city or district.';
  return errors;
}

function makeOrderId() {
  const now = new Date();
  const stamp = `${String(now.getFullYear()).slice(2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(
    now.getDate()
  ).padStart(2, '0')}`;
  return `EV-${stamp}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`;
}

function OrderSummary({ cart, form, subtotal, shippingFee, total, editable, onChangeQty, onRemoveItem }) {

  return (
    <aside className="summary" aria-label="Order summary">
      <h2 className="summary__title">{editable ? 'Your order' : 'Invoice'}</h2>

      <ul className="summary__items">
        {cart.map(({ product, qty, variant }) => (
          <li className="summary__item" key={`${product.id}-${variant?.color ?? 'base'}-${variant?.size ?? ''}`}>
            <div className="summary__desc">
              <p className="summary__name">{product.name}</p>
              {variant?.name && <p className="summary__option">{variant.name}</p>}
              {variant?.size && <p className="summary__option">Size: {variant.size}</p>}

              {editable ? (
                <div className="summary__controls">
                  <div className="summary__qty">
                    <button
                      type="button"
                      className="summary__step"
                      aria-label={`One less ${product.name}${variant?.name ? `, ${variant.name}` : ''}${variant?.size ? `, ${variant.size}` : ''}`}
                      onClick={() => onChangeQty?.(product.id, -1, variant?.color, variant?.size)}
                    >
                      &minus;
                    </button>
                    <span className="summary__count">{qty}</span>
                    <button
                      type="button"
                      className="summary__step"
                      aria-label={`One more ${product.name}${variant?.name ? `, ${variant.name}` : ''}${variant?.size ? `, ${variant.size}` : ''}`}
                      onClick={() => onChangeQty?.(product.id, 1, variant?.color, variant?.size)}
                    >
                      +
                    </button>
                  </div>

                  <button type="button" className="summary__remove" onClick={() => onRemoveItem?.(product.id, variant?.color, variant?.size)}>
                    Remove
                  </button>
                </div>
              ) : (
                <p className="summary__meta">
                  {qty} x {formatPrice(product.price)}
                </p>
              )}
            </div>

            <p className="summary__line">
              {editable && (
                <span className="summary__line-each">
                  {formatPrice(product.price)} each
                </span>
              )}
              {formatPrice(product.price * qty)}
            </p>
          </li>
        ))}
      </ul>

      <dl className="summary__totals">
        <div className="summary__row">
          <dt>Subtotal</dt>
          <dd>{formatPrice(subtotal)}</dd>
        </div>
        <div className="summary__row">
          <dt>
            Shipping
            {form.governorate ? ` · ${form.governorate}` : ''}
          </dt>
          <dd>{shippingFee === null ? '—' : formatPrice(shippingFee)}</dd>
        </div>
        <div className="summary__row summary__row--total">
          <dt>Total</dt>
          <dd>{formatPrice(total)}</dd>
        </div>
      </dl>

      {editable && shippingFee === null && <p className="summary__note">Pick your governorate and we add the delivery fee to the total.</p>}

      {!editable && (
        <dl className="summary__delivery">
          <div className="summary__delivery-row">
            <dt>Name</dt>
            <dd>{form.fullName}</dd>
          </div>
          <div className="summary__delivery-row">
            <dt>Phone</dt>
            <dd>{form.phone}</dd>
          </div>
          {form.whatsapp && (
            <div className="summary__delivery-row">
              <dt>WhatsApp</dt>
              <dd>{form.whatsapp}</dd>
            </div>
          )}
          <div className="summary__delivery-row">
            <dt>Address</dt>
            <dd>{[form.governorate, form.city, form.address].filter(Boolean).join(', ')}</dd>
          </div>
          {form.notes && (
            <div className="summary__delivery-row">
              <dt>Notes</dt>
              <dd>{form.notes}</dd>
            </div>
          )}
        </dl>
      )}
    </aside>
  );
}

function Checkout({ cart = [], onChangeQty, onRemoveItem }) {
  const { governorates, getFee } = useShipping();
  const { whatsapp, methods } = usePayment();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState('details');
  const [method, setMethod] = useState('');
  const [transferFrom, setTransferFrom] = useState('');
  const [transferError, setTransferError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sent, setSent] = useState(null);
  const receiptInput = useRef(null);
  const activeMethod = methods.find((item) => item.id === method) ?? methods[0] ?? null;

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: undefined }));
  }

  function handleDetailsSubmit(event) {
    event.preventDefault();
    const found = validateDetails(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setStep('invoice');
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  function handleTransferChange(event) {
    setTransferFrom(digitsOnly(event.target.value));
    setTransferError('');
  }

  function handleReceiptChange(event) {
    const file = event.target.files[0];
    if (receipt?.url) URL.revokeObjectURL(receipt.url);
    setReceipt(file ? { file, name: file.name, url: URL.createObjectURL(file) } : null);
  }

  function clearReceipt() {
    if (receipt?.url) URL.revokeObjectURL(receipt.url);
    setReceipt(null);
    if (receiptInput.current) receiptInput.current.value = '';
  }

  async function handleConfirm(event) {
    event.preventDefault();
    if (!PHONE_PATTERN.test(transferFrom)) {
      setTransferError('The phone number you sent from must be 11 digits.');
      return;
    }

    const id = makeOrderId();
    const body = new FormData();
    body.append(
      'order',
      JSON.stringify({
        id,
        items: cart.map(({ product, qty, variant }) => ({
          id: product.id,
          name: product.name,
          color: variant?.color,
          colorName: variant?.name,
          size: variant?.size,
          price: product.price,
          qty,
          lineTotal: product.price * qty,
        })),
        customer: form,
        subtotal,
        shippingFee,
        total,
        transferMethod: activeMethod ? { id: activeMethod.id, label: activeMethod.label, number: activeMethod.number } : null,
        transferFrom,
      })
    );
    if (receipt?.file) body.append('receipt', receipt.file, receipt.name);

    setSending(true);
    setSubmitError('');
    try {
      const response = await fetch('/api/orders', { method: 'POST', body });
      if (!response.ok) throw new Error(`order server replied ${response.status}`);
      setSent({ id });
      window.scrollTo({ top: 0, behavior: 'auto' });
    } catch (error) {
      setSubmitError('We could not reach the order server. Run `npm run server` and try again.');
    } finally {
      setSending(false);
    }
  }

  if (cart.length === 0) {
    return (
      <main className="checkout">
        <div className="checkout__empty">
          <span className="checkout__empty-icon">
            <img className="checkout__empty-logo" src={logo} alt="EVAR" width="62" height="62" />
          </span>
          <h1 className="checkout__title">Your cart is empty</h1>
          <p className="checkout__lead">Add something first, then come back here for the delivery details.</p>
          <Link className="btn" to="/">
            Browse the lineup
          </Link>
        </div>
      </main>
    );
  }

  if (sent) {
    const supportLink = waLink(whatsapp, `Hi, I need help with order ${sent.id}.`);
    return (
      <main className="checkout">
        <div className="checkout__sent">
          <span className="checkout__empty-icon">
            <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true">
              <path
                d="M5 12.8l4.2 4.2L19.4 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <h1 className="checkout__title">{`Order ${sent.id} confirmed`}</h1>
          <p className="checkout__lead">
            {'Thank you for choosing EVAR. We have received your order and transfer receipt. Our team will call '}
            <strong>{form.phone}</strong>
            {' to confirm the details. Once confirmed, your order will be prepared for delivery, and the remaining balance is paid to the courier upon arrival.'}
          </p>
          <Link className="btn" to="/">
            Back to the store
          </Link>
          {supportLink && (
          <div className="checkout__support">
            <p className="checkout__support-prompt">
              Need support?
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 3v12m0 0 5-5m-5 5-5-5" /></svg>
            </p>
            <a
              className="checkout__whatsapp"
              href={supportLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Contact support on WhatsApp"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.5 3.5A11.8 11.8 0 0 0 12.1 0C5.5 0 .1 5.4.1 12c0 2.1.6 4.2 1.6 6L0 24l6.2-1.6a12 12 0 0 0 5.9 1.5h.1c6.6 0 12-5.4 12-12a11.9 11.9 0 0 0-3.7-8.4ZM12.2 21.8a10 10 0 0 1-5.1-1.4l-.4-.2-3.7 1 1-3.6-.2-.4A9.8 9.8 0 0 1 2.3 12a9.9 9.9 0 0 1 16.9-7 9.8 9.8 0 0 1 2.9 7 9.9 9.9 0 0 1-9.9 9.8Zm5.4-7.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.2-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.8-.7-1.4-1.6-1.5-1.9-.2-.3 0-.4.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.1-.7-1.6-.9-2.1-.2-.5-.5-.4-.7-.4h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1 2.8 1.1 3c.1.2 2 3.1 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.1-1.4-.1-.1-.3-.2-.6-.3Z" /></svg>
              <span>WhatsApp</span>
              <svg className="checkout__whatsapp-arrow" viewBox="0 0 20 20" aria-hidden="true"><path d="M6 14 14 6M7 6h7v7" /></svg>
            </a>
          </div>
          )}
        </div>
      </main>
    );
  }

  const subtotal = getCartSubtotal(cart);
  const shippingFee = getFee(form.governorate);
  const total = subtotal + (shippingFee ?? 0);
  const invoiceReady = PHONE_PATTERN.test(transferFrom) && receipt !== null;

  if (step === 'invoice') {
    return (
      <main className="checkout">
        <div className="checkout__head">
          <h1 className="checkout__title">Reserve your order</h1>
          <p className="checkout__lead">Transfer the shipping deposit, upload the receipt, and the order is locked for you.</p>
          <button type="button" className="checkout__back" onClick={() => setStep('details')}>
            → Edit your details
          </button>
        </div>

        <div className="checkout__layout">
          <section className="deposit">
            <p className="deposit__fee">
              Deposit amount
              <strong>{formatPrice(shippingFee)}</strong>
            </p>

            {methods.length > 0 ? (
              <>
                <div className="deposit__methods" role="radiogroup" aria-label="Transfer method">
                  {methods.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      role="radio"
                      aria-checked={activeMethod?.id === item.id}
                      className={`deposit__method${activeMethod?.id === item.id ? ' is-active' : ''}`}
                      onClick={() => setMethod(item.id)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <p className="deposit__number">{activeMethod.number}</p>
              </>
            ) : (
              <p className="deposit__text">{'We will send you the transfer number on WhatsApp.'}</p>
            )}
            <p className="deposit__hint">{`The rest (${formatPrice(subtotal)}) stays cash on delivery — you pay the courier when the box arrives.`}</p>

            <form className="deposit__form" onSubmit={handleConfirm}>
              <div className="field field--full">
                <label className="field__label" htmlFor="transferFrom">
                  Phone number you sent the transfer from
                </label>
                <input
                  className={`field__input${transferError ? ' field__input--invalid' : ''}`}
                  id="transferFrom"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="01xxxxxxxxx"
                  maxLength="11"
                  value={transferFrom}
                  onChange={handleTransferChange}
                  aria-invalid={transferError ? true : undefined}
                  required
                />
                {transferError && <p className="field__error">{transferError}</p>}
              </div>

              <div className="field field--full">
                <span className="field__label" id="receiptLabel">
                  Upload a screenshot of the transfer
                </span>
                <div className="upload__box">
                  <label className="upload" htmlFor="receipt">
                    {receipt ? (
                      <span className="upload__filled">
                        <img className="upload__thumb" src={receipt.url} alt="" />
                        <span className="upload__name">{receipt.name}</span>
                      </span>
                    ) : (
                      <span className="upload__empty">Tap to pick the screenshot of the transfer</span>
                    )}
                  </label>

                  {receipt && (
                    <button
                      type="button"
                      className="upload__clear"
                      aria-label={`Remove ${receipt.name}`}
                      onClick={clearReceipt}
                    >
                      <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
                        <path
                          d="M3.5 3.5l9 9M12.5 3.5l-9 9"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                <input
                  className="upload__input"
                  id="receipt"
                  ref={receiptInput}
                  type="file"
                  accept="image/*"
                  aria-labelledby="receiptLabel"
                  onChange={handleReceiptChange}
                  required
                />
              </div>

              <div className="checkout__actions">
                <button className="btn" type="submit" disabled={!invoiceReady || sending}>
                  {sending ? 'Sending…' : 'Confirm order'}
                </button>
                {!invoiceReady && !sending && (
                  <p className="deposit__required">Transfer number and screenshot are both required.</p>
                )}
              </div>

              {submitError && <p className="field__error">{submitError}</p>}
            </form>
          </section>

          <OrderSummary
            cart={cart}
            form={form}
            subtotal={subtotal}
            shippingFee={shippingFee}
            total={total}
            editable={false}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="checkout">
      <div className="checkout__head">
        <h1 className="checkout__title">Checkout</h1>
        <p className="checkout__lead">Fill in your delivery details. You pay the courier when the box arrives.</p>
        <Link className="checkout__back" to="/">
          ← Continue shopping
        </Link>
      </div>

      <div className="checkout__layout">
        <form className="checkout__form" onSubmit={handleDetailsSubmit} noValidate>
          <div className="field">
            <label className="field__label" htmlFor="fullName">
              Full name
            </label>
            <input
              className={`field__input${errors.fullName ? ' field__input--invalid' : ''}`}
              id="fullName"
              name="fullName"
              type="text"
              autoComplete="name"
              placeholder="Full name"
              value={form.fullName}
              onChange={update}
              aria-invalid={errors.fullName ? true : undefined}
              required
            />
            {errors.fullName && <p className="field__error">{errors.fullName}</p>}
          </div>

          <div className="field">
            <label className="field__label" htmlFor="phone">
              Phone number
            </label>
            <input
              className={`field__input${errors.phone ? ' field__input--invalid' : ''}`}
              id="phone"
              name="phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="01xxxxxxxxx"
              maxLength="11"
              value={form.phone}
              onChange={(event) =>
                update({ target: { name: 'phone', value: digitsOnly(event.target.value) } })
              }
              aria-invalid={errors.phone ? true : undefined}
              required
            />
            {errors.phone && <p className="field__error">{errors.phone}</p>}
          </div>

          <div className="field">
            <label className="field__label" htmlFor="whatsapp">
              WhatsApp number <span className="field__optional">optional</span>
            </label>
            <input
              className={`field__input${errors.whatsapp ? ' field__input--invalid' : ''}`}
              id="whatsapp"
              name="whatsapp"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="01xxxxxxxxx"
              maxLength="11"
              value={form.whatsapp}
              onChange={(event) =>
                update({ target: { name: 'whatsapp', value: digitsOnly(event.target.value) } })
              }
              aria-invalid={errors.whatsapp ? true : undefined}
            />
            {errors.whatsapp && <p className="field__error">{errors.whatsapp}</p>}
          </div>

          <div className="field field--full">
            <label className="field__label" htmlFor="governorate">
              Governorate
            </label>
            <select
              className={`field__input${errors.governorate ? ' field__input--invalid' : ''}`}
              id="governorate"
              name="governorate"
              value={form.governorate}
              onChange={update}
              aria-invalid={errors.governorate ? true : undefined}
              required
            >
              <option value="" disabled>
                Choose your governorate
              </option>
              {governorates.map((governorate) => (
                <option value={governorate} key={governorate}>
                  {governorate}
                </option>
              ))}
            </select>
            {errors.governorate && <p className="field__error">{errors.governorate}</p>}
          </div>

          <div className="field field--full">
            <label className="field__label" htmlFor="city">
              City / district
            </label>
            <input
              className={`field__input${errors.city ? ' field__input--invalid' : ''}`}
              id="city"
              name="city"
              type="text"
              autoComplete="address-level2"
              placeholder="El-Mansoura"
              value={form.city}
              onChange={update}
              aria-invalid={errors.city ? true : undefined}
              required
            />
            {errors.city && <p className="field__error">{errors.city}</p>}
          </div>

          <div className="field field--full">
            <label className="field__label" htmlFor="address">
              Full address
            </label>
            <textarea
              className="field__input field__input--area"
              id="address"
              name="address"
              rows="3"
              autoComplete="street-address"
              placeholder="Street, building, floor, flat"
              value={form.address}
              onChange={update}
            />
          </div>

          <div className="field field--full">
            <label className="field__label" htmlFor="notes">
              Notes for the courier <span className="field__optional">optional</span>
            </label>
            <textarea
              className="field__input field__input--area"
              id="notes"
              name="notes"
              rows="2"
              placeholder="Nearest landmark, best time to call"
              value={form.notes}
              onChange={update}
            />
          </div>

          <div className="checkout__actions">
            <button className="btn" type="submit">
              Continue
            </button>
          </div>
        </form>

        <OrderSummary
          cart={cart}
          form={form}
          subtotal={subtotal}
          shippingFee={shippingFee}
          total={total}
          editable
          onChangeQty={onChangeQty}
          onRemoveItem={onRemoveItem}
        />
      </div>
    </main>
  );
}

export default Checkout;
